import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { TeamsAPI } from '@makyoapp/makyo-sdk/api/Teams'
import { TeamAdapter } from '@makyoapp/makyo-common'
import { ImgEasyResult } from '@makyoapp/makyo-ui'

import { queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { useCacheContext } from '@/context/CacheContext'
import { useReqError } from '@/hooks/useReqError'

export function useEditTeam({
  editTeam,
  onBeforeUpdate,
  onAfterUpdate,
}: {
  editTeam?: TeamAdapter
  onBeforeUpdate?: () => void
  onAfterUpdate?: () => void
}) {
  const printAPIError = useReqError()

  const { imageCache } = useCacheContext()

  return useCallback(
    (nextName: string, avatar?: ImgEasyResult, done?: () => void) => {
      if (editTeam) {
        const teamToEdit = { ...editTeam }
        const { id, name, avatar: oldAvatar } = teamToEdit

        onBeforeUpdate?.()

        const promises = []

        if (name !== nextName) {
          promises.push(TeamsAPI.ReqUpdateName(id, nextName))
        }

        if (avatar === undefined && oldAvatar) {
          promises.push(TeamsAPI.ReqDeleteAvatar(id))
        }

        if (avatar?.file !== undefined) {
          promises.push(
            TeamsAPI.ReqUpdateAvatar(id, avatar?.file).then(() => {
              imageCache.update(`team-${id}`, Date.now())
            })
          )
        }

        Promise.all(promises)
          .then(() => {
            toasted('Team updated!')
          })
          .catch((e) => {
            printAPIError(e)
          })

          .finally(() => {
            done?.()
            onAfterUpdate?.()
          })
      }
    },
    [editTeam, onBeforeUpdate, imageCache, printAPIError, onAfterUpdate]
  )
}

export function useDeleteOrTransferTeam() {
  const printAPIError = useReqError()
  const queryClient = useQueryClient()
  return useCallback(
    (
      deleteTarget: TeamAdapter,
      transferTarget?: TeamAdapter,
      options?: {
        onBeforeUpdate?: () => void
        onAfterUpdate?: () => void
        deletedNotice?: string
        transferredNotice?: string
      }
    ) => {
      const {
        onBeforeUpdate,
        onAfterUpdate,
        deletedNotice = 'Team removed!',
        transferredNotice = 'Apps transferred!',
      } = options || {}

      if (!transferTarget) {
        onBeforeUpdate?.()
        TeamsAPI.ReqDelete(deleteTarget.id)
          .then(() => {
            toasted(deletedNotice)
            queryClient.invalidateQueries({ queryKey: [queryKeys.user.trash] })
          })
          .catch((e) => {
            printAPIError(e)
          })
          .finally(() => onAfterUpdate?.())
      } else {
        onBeforeUpdate?.()
        TeamsAPI.ReqTransferApps(deleteTarget.id, transferTarget.id)
          .then(() => {
            toasted(transferredNotice)
            return TeamsAPI.ReqDelete(deleteTarget.id).then(() => {
              toasted(deletedNotice)
              queryClient.invalidateQueries({ queryKey: [queryKeys.user.trash] })
            })
          })
          .catch((e) => {
            printAPIError(e)
          })
          .finally(() => onAfterUpdate?.())
      }
    },
    [printAPIError, queryClient]
  )
}
